import React, { useMemo, useLayoutEffect, useState, useEffect, useCallback, useRef } from "react";
import { View, StyleSheet, TextInput, Pressable, Platform, ScrollView } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { useTheme } from "@/hooks/useTheme";
import { useNavigation } from "@react-navigation/native";
import { StackNavigationProp } from "@react-navigation/stack";
import { Table, ColumnDef } from "@/components/NewTable";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { userService } from "@/services/userService";
import { shiftsService } from "@/services/shiftService";
import Avatar from "@/components/Avatar";
import { useSupervisors } from "../SupervisorsContext";
import { useToolTip } from "@/hooks/useToolTip";
import { ArrayChips } from "@/components/Table/ArrayChips";
import { supabase } from "@/lib/supabase-client";
import useContextMenu from "@/hooks/useContextMenu";
import { SmallToggle } from "@/components/SmallToggle";
import useAuth from "@/hooks/useAuth";
import { FromDB } from "@/types";
import { UserType } from "@/types/db/users";
import useNavigationGuard from "@/hooks/useNavigationGuard";
import { useCurrentEntity } from "@/contexts/CurrentEntityContext";
import { useNavigationHistory } from "@/contexts/NavigationHistoryContext";

type RootStackParamList = {
  AssignedSupervisors: undefined;
  AddSupervisors: undefined;
};

type AvailableUser = FromDB<UserType>;

interface HeaderButtonProps {
  icon: keyof typeof MaterialCommunityIcons.glyphMap;
  tooltip: string;
  onPress: () => void;
  disabled?: boolean;
}

function HeaderButton({ icon, tooltip, onPress, disabled }: HeaderButtonProps) {
  const { colors } = useTheme();
  const toolTip = useToolTip();
  const ref = useRef<View>(null);
  const isHovering = useRef(false);

  const handlePointerEnter = useCallback(() => {
    if (disabled) return;
    isHovering.current = true;
    ref.current?.measureInWindow((x, y, width) => {
      if (!isHovering.current) return;
      const tooltipWidth = tooltip.length * 7 + 16;
      const tooltipHeight = 28;
      toolTip.setLocation({
        width: tooltipWidth,
        height: tooltipHeight,
        x: x + width - tooltipWidth,
        y: y - tooltipHeight - 6,
      });
      toolTip.setToolTipContent(
        <View
          style={{
            backgroundColor: colors.background,
            paddingHorizontal: 8,
            paddingVertical: 4,
            borderRadius: 8,
            borderWidth: StyleSheet.hairlineWidth,
            borderColor: colors.border,
          }}
        >
          <ThemedText style={{ fontSize: 12 }}>{tooltip}</ThemedText>
        </View>,
      );
      toolTip.setVisible(true);
    });
  }, [tooltip, colors.background, colors.border, toolTip, disabled]);

  const handlePointerLeave = useCallback(() => {
    isHovering.current = false;
    toolTip.setVisible(false);
  }, [toolTip]);

  const handlePress = useCallback(() => {
    if (disabled) return;
    isHovering.current = false;
    toolTip.setVisible(false);
    onPress();
  }, [onPress, toolTip, disabled]);

  return (
    <Pressable
      ref={ref}
      style={{ marginRight: 16, padding: 4, opacity: disabled ? 0.4 : 1 }}
      onPress={handlePress}
      onPointerEnter={handlePointerEnter}
      onPointerLeave={handlePointerLeave}
      disabled={disabled}
    >
      <MaterialCommunityIcons name={icon} size={24} color={disabled ? colors.textSecondary : colors.primary} />
    </Pressable>
  );
}

export default function AddSupervisorsScreen() {
  const { colors } = useTheme();
  const { displayNames } = useAuth();
  const navigation = useNavigation<StackNavigationProp<RootStackParamList>>();
  const { supervisors, shiftId, addSupervisor, shiftMarkets, projectMarkets, shiftLocation, projectLocation } =
    useSupervisors();
  const contextMenu = useContextMenu();
  const filterButtonRef = useRef<View>(null);
  const isFilterMenuOpen = useRef(false);
  const currentEntity = useCurrentEntity();
  const { pushNavigation } = useNavigationHistory();
  const { navigateToEntity } = useNavigationGuard();

  // Navigation handler for user names
  const handleUserPress = useCallback(
    (user: AvailableUser) => {
      // Save current entity to navigation history before navigating
      if (currentEntity?.currentPanel && currentEntity?.currentId) {
        pushNavigation({
          panel: currentEntity.currentPanel,
          id: currentEntity.currentId,
          title: currentEntity.currentTitle || currentEntity.currentPanel,
        });
      }
      // Navigate to user profile
      navigateToEntity("user", user.id);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentEntity, pushNavigation],
  );

  // Display name helpers
  const projectName = displayNames?.projects?.name || "Project";
  const shiftName = displayNames?.shifts?.name || "Shift";

  const [users, setUsers] = useState<AvailableUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchValue, setSearchValue] = useState("");
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());

  // Show non-eligible toggle
  const [showNonEligible, setShowNonEligible] = useState(false);

  // Markets filter
  const [allMarkets, setAllMarkets] = useState<{ id: string; name: string }[]>([]);
  const [selectedMarketIds, setSelectedMarketIds] = useState<Set<string> | null>(null);
  const [showNoMarket, setShowNoMarket] = useState(false);

  // Get Component width
  const [width, setWidth] = useState(0);
  const handleLayout = (event) => {
    const { width: componentWidth } = event.nativeEvent.layout;
    setWidth(componentWidth);
  };

  // Get already assigned supervisor IDs
  const assignedSupervisorIds = useMemo(() => {
    return new Set(supervisors.map((s) => s.user_id));
  }, [supervisors]);

  // Fetch all markets from database
  useEffect(() => {
    (async () => {
      const { data } = await supabase.schema("lists").from("markets").select("id, name").order("name");
      if (data) {
        setAllMarkets(data);
      }
    })();
  }, []);

  // Build market ID to name map
  const marketMap = useMemo(() => {
    return Object.fromEntries(allMarkets.map((m) => [m.id, m.name]));
  }, [allMarkets]);

  // Compute deduplicated market list with sources
  const availableMarkets = useMemo(() => {
    type SourceType = "projectLocation" | "project" | "shiftLocation" | "shift";
    const marketSources = new Map<string, { id: string; sources: Set<SourceType> }>();

    const addMarket = (marketId: string | undefined, source: SourceType) => {
      // Only add if market exists in the marketMap (valid market)
      if (!marketId || !marketMap[marketId]) return;
      if (!marketSources.has(marketId)) {
        marketSources.set(marketId, { id: marketId, sources: new Set() });
      }
      marketSources.get(marketId)!.sources.add(source);
    };

    addMarket(projectLocation?.market_id, "projectLocation");
    for (const marketId of projectMarkets || []) {
      addMarket(marketId, "project");
    }
    addMarket(shiftLocation?.market_id, "shiftLocation");
    for (const marketId of shiftMarkets || []) {
      addMarket(marketId, "shift");
    }

    return Array.from(marketSources.values()).map((m) => {
      const sources = Array.from(m.sources);
      let sourceDisplay: string;
      if (sources.length === 1) {
        const source = sources[0];
        if (source === "projectLocation") sourceDisplay = `${projectName} Location`;
        else if (source === "project") sourceDisplay = projectName;
        else if (source === "shiftLocation") sourceDisplay = `${shiftName} Location`;
        else sourceDisplay = shiftName;
      } else if (sources.length === 2 && sources.includes("projectLocation") && sources.includes("shiftLocation")) {
        sourceDisplay = "Both Locations";
      } else if (sources.length === 2 && sources.includes("project") && sources.includes("shift")) {
        sourceDisplay = "Both";
      } else {
        sourceDisplay = "Multiple";
      }
      return {
        id: m.id,
        name: marketMap[m.id],
        source: sourceDisplay,
        sources,
      };
    });
  }, [
    projectLocation?.market_id,
    projectMarkets,
    shiftLocation?.market_id,
    shiftMarkets,
    marketMap,
    projectName,
    shiftName,
  ]);

  // Compute other markets (all markets minus effective markets)
  const otherMarkets = useMemo(() => {
    const effectiveIds = new Set(availableMarkets.map((m) => m.id));
    return allMarkets.filter((m) => !effectiveIds.has(m.id));
  }, [allMarkets, availableMarkets]);

  // Initialize selected markets with all effective markets on first load
  useEffect(() => {
    if (selectedMarketIds === null && availableMarkets.length > 0) {
      setSelectedMarketIds(new Set(availableMarkets.map((m) => m.id)));
    }
  }, [availableMarkets, selectedMarketIds]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const effectiveSelectedMarketIds = selectedMarketIds ?? new Set<string>();

  // Check if filter is modified from default
  const allEffectiveSelected = availableMarkets.every((m) => effectiveSelectedMarketIds.has(m.id));
  const hasOtherMarketsSelected = otherMarkets.some((m) => effectiveSelectedMarketIds.has(m.id));
  const hasActiveFilter = !allEffectiveSelected || hasOtherMarketsSelected || showNoMarket;

  // Build filter menu content
  const filterMenuContent = useMemo(
    () => (
      <View style={styles.filterPopover}>
        <View style={[styles.filterHeader, { borderBottomColor: colors.border }]}>
          <ThemedText type="defaultSemiBold" style={{ fontSize: 14 }}>
            Filter by Market
          </ThemedText>
          {hasActiveFilter && (
            <Pressable
              onPress={() => {
                setSelectedMarketIds(new Set(availableMarkets.map((m) => m.id)));
                setShowNoMarket(false);
              }}
            >
              <ThemedText style={{ fontSize: 12, color: colors.primary }}>Reset</ThemedText>
            </Pressable>
          )}
        </View>

        <ScrollView style={{ maxHeight: 400 }} showsVerticalScrollIndicator>
          {/* Effective Markets Section */}
          {availableMarkets.length > 0 && (
            <>
              <View style={[styles.sectionDivider, { borderBottomColor: colors.border }]}>
                <ThemedText
                  style={{
                    fontSize: 11,
                    color: colors.textSecondary,
                    fontWeight: "600",
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                  }}
                >
                  Effective Markets
                </ThemedText>
              </View>
              {availableMarkets.map((market) => (
                <Pressable
                  key={market.id}
                  style={styles.filterOption}
                  onPress={() => {
                    setSelectedMarketIds((prev) => {
                      const current = prev ?? new Set<string>();
                      const next = new Set(current);
                      if (next.has(market.id)) next.delete(market.id);
                      else next.add(market.id);
                      return next;
                    });
                  }}
                >
                  <MaterialCommunityIcons
                    name={effectiveSelectedMarketIds.has(market.id) ? "checkbox-marked" : "checkbox-blank-outline"}
                    size={20}
                    color={effectiveSelectedMarketIds.has(market.id) ? colors.primary : colors.textSecondary}
                  />
                  <ThemedText style={{ flex: 1, fontSize: 13 }} numberOfLines={1}>
                    {market.name}
                  </ThemedText>
                  <View
                    style={[
                      styles.sourceChip,
                      {
                        backgroundColor:
                          market.sources.includes("projectLocation") || market.sources.includes("shiftLocation")
                            ? "#9C27B020"
                            : market.source === "Both" || market.source === "Multiple"
                              ? colors.primary + "20"
                              : market.sources.includes("project")
                                ? "#4CAF5020"
                                : "#FF980020",
                      },
                    ]}
                  >
                    <ThemedText
                      style={{
                        fontSize: 10,
                        color:
                          market.sources.includes("projectLocation") || market.sources.includes("shiftLocation")
                            ? "#9C27B0"
                            : market.source === "Both" || market.source === "Multiple"
                              ? colors.primary
                              : market.sources.includes("project")
                                ? "#4CAF50"
                                : "#FF9800",
                      }}
                    >
                      {market.source}
                    </ThemedText>
                  </View>
                </Pressable>
              ))}
            </>
          )}

          {/* All Markets Section */}
          {otherMarkets.length > 0 && (
            <>
              <View style={[styles.sectionDivider, { borderBottomColor: colors.border }]}>
                <ThemedText
                  style={{
                    fontSize: 11,
                    color: colors.textSecondary,
                    fontWeight: "600",
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                  }}
                >
                  All Markets
                </ThemedText>
              </View>
              {otherMarkets.map((market) => (
                <Pressable
                  key={market.id}
                  style={styles.filterOption}
                  onPress={() => {
                    setSelectedMarketIds((prev) => {
                      const current = prev ?? new Set<string>();
                      const next = new Set(current);
                      if (next.has(market.id)) next.delete(market.id);
                      else next.add(market.id);
                      return next;
                    });
                  }}
                >
                  <MaterialCommunityIcons
                    name={effectiveSelectedMarketIds.has(market.id) ? "checkbox-marked" : "checkbox-blank-outline"}
                    size={20}
                    color={effectiveSelectedMarketIds.has(market.id) ? colors.primary : colors.textSecondary}
                  />
                  <ThemedText style={{ flex: 1, fontSize: 13 }} numberOfLines={1}>
                    {market.name}
                  </ThemedText>
                </Pressable>
              ))}
            </>
          )}

          {/* Other Section */}
          <View style={[styles.sectionDivider, { borderBottomColor: colors.border }]}>
            <ThemedText
              style={{
                fontSize: 11,
                color: colors.textSecondary,
                fontWeight: "600",
                textTransform: "uppercase",
                letterSpacing: 0.5,
              }}
            >
              Other
            </ThemedText>
          </View>
          <Pressable style={styles.filterOption} onPress={() => setShowNoMarket((v) => !v)}>
            <MaterialCommunityIcons
              name={showNoMarket ? "checkbox-marked" : "checkbox-blank-outline"}
              size={20}
              color={showNoMarket ? colors.primary : colors.textSecondary}
            />
            <ThemedText style={{ flex: 1, fontSize: 13 }}>No Market</ThemedText>
            <View style={[styles.sourceChip, { backgroundColor: colors.backgroundSecondary }]}>
              <ThemedText style={{ fontSize: 10, color: colors.textSecondary }}>Users without market</ThemedText>
            </View>
          </Pressable>

          {availableMarkets.length === 0 && otherMarkets.length === 0 && (
            <View style={styles.filterEmpty}>
              <ThemedText style={{ fontSize: 12, color: colors.textSecondary }}>No markets configured</ThemedText>
            </View>
          )}
        </ScrollView>
      </View>
    ),
    [colors, hasActiveFilter, showNoMarket, availableMarkets, effectiveSelectedMarketIds, otherMarkets],
  );

  // Close context menu when this screen unmounts to prevent it persisting across navigation
  useEffect(() => {
    return () => {
      if (isFilterMenuOpen.current) {
        contextMenu.setVisible(false);
        isFilterMenuOpen.current = false;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update context menu content when filter state changes (only for our filter menu)
  useEffect(() => {
    if (contextMenu.visible && isFilterMenuOpen.current) {
      contextMenu.setMenuContent(filterMenuContent);
    }
    if (!contextMenu.visible) {
      isFilterMenuOpen.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterMenuContent, contextMenu.visible]);

  // Fetch available users
  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true);
      setError(null);

      try {
        const data = await userService.getUsers();
        // Filter to only active users, optionally filtering by supervisor eligible
        const activeUsers = data.filter((u) => u.status === "ACTIVE");
        setUsers(activeUsers);
      } catch (err: any) {
        console.error("Failed to fetch users:", err);
        setError(err.message || "Failed to load users");
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  const handleAddSelectedUsers = useCallback(async () => {
    if (!shiftId || selectedUserIds.size === 0) return;

    const userIdsArray = Array.from(selectedUserIds);

    try {
      for (const userId of userIdsArray) {
        const result = await shiftsService.addShiftSupervisor(shiftId, userId);
        const user = users.find((u) => u.id === userId);
        addSupervisor({
          user_id: userId,
          user: result.user || {
            id: userId,
            first_name: user?.first_name,
            last_name: user?.last_name,
            preferred_name: user?.preferred_name,
            email_address: user?.email_address,
            meta_data: user?.meta_data as Record<string, unknown> | null,
          },
          permissions: result.permissions || {},
          meta_data: result.meta_data || {},
        });
      }

      // Remove added users from available list
      setUsers((prev) => prev.filter((u) => !selectedUserIds.has(u.id)));
      setSelectedUserIds(new Set());
    } catch (err) {
      console.error("Failed to add supervisors:", err);
    }
  }, [shiftId, selectedUserIds, users, addSupervisor]);

  // Set up header
  useLayoutEffect(() => {
    navigation.setOptions({
      headerTitle: () => (
        <View style={styles.headerTitleContainer}>
          <View style={styles.headerTitleText}>
            <ThemedText style={styles.title}>Add Supervisors</ThemedText>
            <ThemedText style={[styles.subtitle, { color: colors.textSecondary }]}>
              {users.length} available user{users.length !== 1 ? "s" : ""}
            </ThemedText>
          </View>
          <View style={[styles.headerSearchContainer, { backgroundColor: colors.backgroundSecondary }]}>
            <MaterialCommunityIcons
              name="magnify"
              size={16}
              color={colors.textSecondary}
              style={styles.headerSearchIcon}
            />
            <TextInput
              value={searchValue}
              onChangeText={setSearchValue}
              placeholder="Search..."
              placeholderTextColor={colors.textSecondary}
              style={[styles.headerSearchInput, { color: colors.text }]}
              autoCapitalize="none"
              autoCorrect={false}
            />
            {searchValue.length > 0 && (
              <Pressable onPress={() => setSearchValue("")} style={styles.headerClearButton}>
                <MaterialCommunityIcons name="close-circle" size={14} color={colors.textSecondary} />
              </Pressable>
            )}
          </View>
        </View>
      ),
      headerRight: () => (
        <View style={{ flexDirection: "row", alignItems: "center", gap: 16, marginRight: 8 }}>
          <Pressable
            ref={filterButtonRef}
            style={{ flexDirection: "row", alignItems: "center", gap: 6 }}
            onPress={() => {
              filterButtonRef.current?.measureInWindow((x, y, width, height) => {
                isFilterMenuOpen.current = true;
                contextMenu.setTriggerElement?.({ x, y, width, height });
                contextMenu.setLocation({ x, y: y + height + 4 });
                contextMenu.setMenuContent(filterMenuContent);
                contextMenu.setVisible(true);
              });
            }}
          >
            <MaterialCommunityIcons
              name="filter-variant"
              size={20}
              color={hasActiveFilter ? colors.primary : colors.text}
            />
            <ThemedText
              style={{
                fontSize: 14,
                opacity: hasActiveFilter ? 1 : 0.8,
                color: hasActiveFilter ? colors.primary : colors.text,
              }}
            >
              Markets ({effectiveSelectedMarketIds.size}
              {showNoMarket ? "+1" : ""})
            </ThemedText>
          </Pressable>
          <SmallToggle value={showNonEligible} onValueChange={setShowNonEligible} label="Non-Eligible" />
          <HeaderButton
            icon="plus"
            tooltip={
              selectedUserIds.size > 0
                ? `Add ${selectedUserIds.size} Supervisor${selectedUserIds.size > 1 ? "s" : ""}`
                : "Select users to add"
            }
            onPress={handleAddSelectedUsers}
            disabled={selectedUserIds.size === 0}
          />
        </View>
      ),
    });
  }, [
    navigation,
    users.length,
    colors,
    searchValue,
    selectedUserIds.size,
    handleAddSelectedUsers,
    hasActiveFilter,
    effectiveSelectedMarketIds.size,
    showNoMarket,
    showNonEligible,
    filterMenuContent,
    contextMenu,
  ]);

  const formatUserName = (user: AvailableUser) => {
    const firstName = user.preferred_name || user.first_name || "";
    const lastName = user.last_name || "";
    return `${firstName} ${lastName}`.trim() || "—";
  };

  const handleAddUser = async (user: AvailableUser) => {
    if (!shiftId) {
      console.error("Missing shiftId");
      return;
    }

    try {
      const result = await shiftsService.addShiftSupervisor(shiftId, user.id);

      addSupervisor({
        user_id: user.id,
        user: result.user || {
          id: user.id,
          first_name: user.first_name,
          last_name: user.last_name,
          preferred_name: user.preferred_name,
          email_address: user.email_address,
          meta_data: user.meta_data as Record<string, unknown> | null,
        },
        permissions: result.permissions || {},
        meta_data: result.meta_data || {},
      });

      // Remove user from available list
      setUsers((prev) => prev.filter((u) => u.id !== user.id));
      setSelectedUserIds((prev) => {
        const next = new Set(prev);
        next.delete(user.id);
        return next;
      });
    } catch (err: any) {
      console.error("Failed to add supervisor:", err);
      setError(err.message || "Failed to add supervisor");
    }
  };

  const columns: ColumnDef<AvailableUser>[] = useMemo(() => {
    return [
      {
        key: "avatar",
        header: "",
        width: 48,
        align: "center",
        sortable: false,
        searchable: false,
        render: (_, row) => (
          <Avatar
            userId={row.id}
            email={row.email_address ?? undefined}
            firstName={row.preferred_name || row.first_name || ""}
            lastName={row.last_name || ""}
            size={30}
          />
        ),
      },
      {
        key: "name",
        header: "Name",
        flex: 1,
        minWidth: 120,
        accessor: (row) => formatUserName(row),
        onPress: handleUserPress,
      },
      {
        key: "eligible",
        header: "Eligible",
        minWidth: 110,
        flex: 1,
        align: "center",
        searchable: false,
        accessor: (row) => ((row.meta_data as any)?.supervisor_eligible ? 1 : 0),
        render: (_, row) => {
          const isEligible = (row.meta_data as any)?.supervisor_eligible === true;
          return isEligible ? (
            <MaterialCommunityIcons name="check-circle" size={18} color={colors.complete.border} />
          ) : (
            <ThemedText style={{ fontSize: 12, opacity: 0.5 }}>—</ThemedText>
          );
        },
      },
      {
        key: "email",
        header: "Email",
        flex: 3,
        minWidth: 250,
        accessor: (row) => row.email_address || "—",
      },
      {
        key: "markets",
        header: "Markets",
        flex: 5,
        width: 180,
        searchable: true,
        searchValue: (_, row) => {
          const markets = (row.meta_data as any)?.markets || [];
          return markets.map((m: string) => marketMap[m] || m).join(" ");
        },
        render: (_, row) => {
          const markets = (row.meta_data as any)?.markets || [];
          if (markets.length === 0) return <ThemedText style={{ fontSize: 12 }}>—</ThemedText>;
          return <ArrayChips values={markets} displayMap={marketMap} chipColor="#4169E1" width={width} />;
        },
      },
    ];
  }, [marketMap, handleUserPress, colors.complete.border, width]);

  // Filter users by eligibility, markets and search
  const filteredUsers = useMemo(() => {
    let result = users;

    // Filter out already assigned supervisors
    result = result.filter((u) => !assignedSupervisorIds.has(u.id));

    // Filter by supervisor eligibility (unless showNonEligible is enabled)
    if (!showNonEligible) {
      result = result.filter((u) => (u.meta_data as any)?.supervisor_eligible === true);
    }

    // Apply market filter
    if (effectiveSelectedMarketIds.size > 0 || showNoMarket) {
      result = result.filter((user) => {
        const userMarkets: string[] = (user.meta_data as any)?.markets || [];

        if (showNoMarket && userMarkets.length === 0) return true;

        if (effectiveSelectedMarketIds.size > 0) {
          return userMarkets.some((m) => effectiveSelectedMarketIds.has(m));
        }

        return false;
      });
    }

    // Apply search filter
    if (searchValue.trim()) {
      const search = searchValue.toLowerCase();
      result = result.filter((user) => {
        const name = formatUserName(user).toLowerCase();
        const email = (user.email_address || "").toLowerCase();
        return name.includes(search) || email.includes(search);
      });
    }

    return result;
  }, [users, searchValue, effectiveSelectedMarketIds, showNoMarket, assignedSupervisorIds, showNonEligible]);

  if (error) {
    return (
      <View style={[styles.container, { backgroundColor: colors.backgroundSecondary }]}>
        <View style={styles.emptyState}>
          <MaterialCommunityIcons name="alert-circle" size={48} color={colors.red.text} />
          <ThemedText style={styles.emptyText}>Failed to load users</ThemedText>
          <ThemedText style={{ opacity: 0.6, fontSize: 12 }}>{error}</ThemedText>
        </View>
      </View>
    );
  }

  return (
    <View style={[styles.container, { backgroundColor: colors.backgroundSecondary }]} onLayout={handleLayout}>
      <Table<AvailableUser>
        data={filteredUsers}
        columns={columns}
        keyExtractor={(item) => item.id}
        loading={loading}
        loadingRowCount={8}
        sortable
        defaultSort={{ key: "name", direction: "asc" }}
        emptyMessage="No available users"
        emptyIcon="account-outline"
        selectable
        selectedKeys={selectedUserIds}
        onSelectionChange={setSelectedUserIds}
        rowActions={[
          {
            icon: "plus",
            label: "Add as Supervisor",
            onPress: (row) => handleAddUser(row),
          },
        ]}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  headerTitleContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  headerTitleText: {
    gap: 2,
  },
  title: {
    fontSize: 16,
    fontWeight: "600",
  },
  subtitle: {
    fontSize: 12,
  },
  headerSearchContainer: {
    flexDirection: "row",
    alignItems: "center",
    borderRadius: 8,
    paddingHorizontal: 10,
    paddingVertical: 6,
    minWidth: 140,
  },
  headerSearchIcon: {
    marginRight: 6,
  },
  headerSearchInput: {
    flex: 1,
    fontSize: 14,
    minWidth: 100,
    ...Platform.select({
      web: { outlineStyle: "none" as any },
    }),
  },
  headerClearButton: {
    padding: 2,
    marginLeft: 4,
  },
  emptyState: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 32,
    gap: 8,
  },
  emptyText: {
    fontSize: 16,
    fontWeight: "600",
    opacity: 0.6,
  },
  filterPopover: {
    width: 400,
    maxHeight: 320,
  },
  filterHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  sectionDivider: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderBottomWidth: StyleSheet.hairlineWidth,
    backgroundColor: "rgba(0,0,0,0.02)",
  },
  filterOption: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  filterEmpty: {
    paddingHorizontal: 12,
    paddingVertical: 16,
    alignItems: "center",
  },
  sourceChip: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
  },
});
